export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string | undefined,
    public readonly retryable: boolean
  ) {
    super(message);
  }
}

type RequestIdCrypto = Pick<Crypto, "getRandomValues"> & {
  randomUUID?: () => string;
};

export function createRequestId(
  cryptoApi: RequestIdCrypto | null | undefined = globalThis.crypto
): string {
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }

  if (typeof cryptoApi?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return `web-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
  }

  return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      "x-request-id": createRequestId(),
      ...init?.headers
    }
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as
      | { code?: string; message?: string; request_id?: string; retryable?: boolean }
      | null;
    throw new ApiError(
      response.status,
      body?.code ?? "HTTP_ERROR",
      body?.message ?? `请求失败（HTTP ${response.status}）`,
      body?.request_id,
      body?.retryable ?? response.status >= 500
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export type Session = { subject: string; display_name: string; role: "VIEWER" | "OPERATOR" | "ADMIN" };

export type AiModelRelease = {
  id: string;
  version: string;
  model_id: string;
  runtime_abi: string;
  checkpoint_sha256: string;
  config_sha256: string;
  status: string;
  created_at: string;
  validated_at: string | null;
  is_active: boolean;
  is_prewarm_target: boolean;
  ready_nodes: number;
  pending_jobs: number;
  last_error: string | null;
};

export type AdminOverview = {
  queued_jobs: number;
  running_jobs: number;
  normal_queued_jobs: number;
  ai_queued_jobs: number;
  normal_running_jobs: number;
  ai_running_jobs: number;
  succeeded_jobs_24h: number;
  failed_jobs_24h: number;
  oldest_queued_seconds: number | null;
  ready_nodes: number;
  total_nodes: number;
  total_slots: number;
  busy_slots: number;
  normal_ready_nodes: number;
  ai_ready_nodes: number;
  normal_total_slots: number;
  normal_busy_slots: number;
  ai_total_slots: number;
  ai_busy_slots: number;
  callback_pending: number;
  callback_dead_letter: number;
  active_storage_versions: number;
};

export type AutoDlSettings = {
  enabled: boolean;
  token_configured: boolean;
  image_uuid: string | null;
  image_verified: boolean;
  gpu_spec_uuid: string;
  expand_system_disk_by_gb: number;
  workers_per_node: number;
  backlog_jobs_per_node: number;
  min_nodes: number;
  max_nodes: number;
  scale_out_queue_threshold: number;
  scale_out_sustain_seconds: number;
  scale_in_queue_threshold: number;
  scale_in_sustain_seconds: number;
  cooldown_seconds: number;
  min_lifetime_seconds: number;
  node_ready_timeout_seconds: number;
  above_threshold_since: string | null;
  below_threshold_since: string | null;
  cooldown_until: string | null;
  last_evaluated_at: string | null;
  last_decision: string | null;
  last_error: string | null;
  version: number;
  updated_at: string;
};

export type AutoDlImage = { image_uuid: string; name: string; size_bytes: number | null; verified: boolean };
export type AutoDlConnection = { images: AutoDlImage[] };
export type AutoDlInstance = {
  instance_uuid: string; instance_name: string; image_uuid: string;
  ownership: "AUTO" | "MANUAL"; status: string; node_id: string | null;
  node_name: string | null; node_status: string | null; active_attempts: number;
  cloud_status: string | null; created_at: string; ready_at: string | null; updated_at: string;
};
export type AutoDlInstances = { items: AutoDlInstance[]; cloud_error: string | null };
export type AutoDlAction = {
  action_id: string; action_type: string; status: string; ownership: string;
  instance_name: string | null; instance_uuid: string | null; image_uuid: string | null;
  reason: string; requested_by: string; last_error: string | null; attempt_count: number;
  created_at: string; updated_at: string; finished_at: string | null;
};

export type AdminJob = {
  job_id: string;
  external_id: string;
  status: string;
  duration_ms: number | null;
  output_format: string;
  node_type: string;
  execution_device: "cpu" | "cuda" | null;
  server_denoise_enabled: boolean;
  mp3_bitrate_kbps: number;
  attempt_count: number;
  max_attempts: number;
  input_url: string;
  callback_url: string | null;
  error_code: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
};

export type AdminJobsResponse = {
  items: AdminJob[];
  next_cursor: string | null;
};

export type CreatedJob = {
  job_id: string;
  external_id: string;
  node_type: string;
  status: string;
  created_at: string;
};

export type AdminBatchJobsResponse = {
  batch_id: string;
  requested: number;
  jobs: CreatedJob[];
};

export type CallbackSettings = {
  dispatch_enabled: boolean;
  default_callback_enabled: boolean;
  default_url: string | null;
  signing_secret_configured: boolean;
  max_concurrency: number;
  request_timeout_seconds: number;
  retry_schedule_seconds: number[];
  max_retry_window_seconds: number;
  timezone: string;
  version: number;
  updated_at: string;
};

export type CallbackDelivery = {
  event_id: string;
  job_id: string;
  external_id: string;
  event_type: string;
  status: string;
  callback_url: string | null;
  attempt_count: number;
  available_at: string;
  last_error: string | null;
  last_http_status: number | null;
  response_latency_ms: number | null;
  last_attempt_at: string | null;
  delivered_at: string | null;
  created_at: string;
  updated_at: string;
};

export type CallbackDeliveriesResponse = {
  items: CallbackDelivery[];
  next_cursor: string | null;
};

export type CallbackDeliveryDetail = CallbackDelivery & {
  processing_log: unknown;
  last_request_payload: unknown | null;
  last_request_headers: unknown | null;
  last_response_body: string | null;
  last_response_content_type: string | null;
  last_response_truncated: boolean;
};

export type AdminAttempt = {
  attempt_id: string;
  attempt_no: number;
  status: string;
  node_id: string | null;
  node_name: string | null;
  pool_id: string | null;
  execution_device: "cpu" | "cuda" | null;
  inference_backend: string | null;
  gpu_uuid: string | null;
  lease_expires_at: string;
  error_code: string | null;
  error_message: string | null;
  metrics: Record<string, unknown>;
  output_object_key: string | null;
  output_size_bytes: number | null;
  pitch_output_object_key: string | null;
  pitch_output_size_bytes: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string;
};

export type AdminJobResult = {
  object_key: string | null;
  size_bytes: number | null;
  download_url: string | null;
  download_url_expires_at: string | null;
  download_error: string | null;
  pitch_object_key: string | null;
  pitch_size_bytes: number | null;
  pitch_download_url: string | null;
  pitch_download_url_expires_at: string | null;
  pitch_download_error: string | null;
  result_expires_at: string | null;
  cleanup_status: string;
};

export type AdminJobDetail = AdminJob & {
  model_release_id: string | null;
  inference_backend: string | null;
  gpu_uuid: string | null;
  retention_days: number;
  pitch_ratio: number;
  dsp_config: TranscodeSettings["dsp_config"];
  pipeline_version: string;
  current_attempt_id: string | null;
  error_message: string | null;
  result: AdminJobResult | null;
  attempts: AdminAttempt[];
};

export type AdminNode = {
  node_id: string;
  pool_id: string;
  name: string;
  node_type: string;
  execution_device: "cpu" | "cuda" | null;
  gpu_uuid: string | null;
  provider_instance_id: string | null;
  scaling_mode: "AUTO" | "MANUAL";
  status: string;
  pipeline_version: string;
  agent_version: string;
  image_version: string;
  expected_image_version: string;
  cpu_cores: number | null;
  total_slots: number;
  desired_slots: number;
  busy_slots: number;
  free_slots: number;
  cpu_percent: number | null;
  rss_bytes: number | null;
  open_fds: number | null;
  spool_free_bytes: number | null;
  input_cache_bytes: number | null;
  last_spool_cleanup_at: string | null;
  last_spool_cleanup_files_removed: number | null;
  last_spool_cleanup_bytes_removed: number | null;
  last_spool_cleanup_failures: number | null;
  active_attempts: number;
  last_heartbeat_at: string | null;
};

export type NodeSpoolSettings = {
  version: number;
  cleanup_interval_seconds: number;
  input_cache_retention_hours: number;
  input_cache_max_bytes: number;
  min_spool_free_bytes: number;
  updated_at: string;
};

export type NodeConcurrencyUpdate = {
  node_id: string;
  reported_slots: number;
  desired_slots: number;
  reconfiguration_pending: boolean;
};

export type CleanupInvalidNodesResult = {
  archived_nodes: number;
  skipped_active_nodes: number;
};

export type NodeAgentRelease = {
  revision: number;
  image_repository: string;
  image_tag: string;
  image: string;
  updated_at: string;
};

export type TencentAsSettings = {
  enabled: boolean;
  pool_id: string;
  region: string;
  auto_scaling_group_id: string | null;
  secret_id_hint: string | null;
  credentials_configured: boolean;
  scale_out_queue_threshold: number;
  scale_out_sustain_seconds: number;
  scale_out_step: number;
  backlog_jobs_per_node: number;
  scale_in_queue_threshold: number;
  scale_in_sustain_seconds: number;
  cooldown_seconds: number;
  min_nodes: number;
  max_nodes: number;
  node_ready_timeout_seconds: number;
  workers_per_node: number;
  above_threshold_since: string | null;
  below_threshold_since: string | null;
  cooldown_until: string | null;
  policy_last_evaluated_at: string | null;
  last_decision: string | null;
  policy_last_error: string | null;
  version: number;
  updated_at: string;
};

export type TencentAsCapacity = {
  auto_scaling_group_id: string;
  auto_scaling_group_name: string;
  min_size: number;
  desired_capacity: number;
  current_instances: number;
  in_service_instances: number;
  max_size: number;
  status: string;
  in_activity_status: string;
  instance_ids: string[];
};

export type TencentAsGroup = {
  auto_scaling_group_id: string;
  auto_scaling_group_name: string;
  min_size: number;
  desired_capacity: number;
  in_service_instance_count: number;
  max_size: number;
  status: string;
  in_activity_status: string;
};

export type AutoscalingAction = {
  action_id: string;
  pool_id: string;
  action_type: string;
  status: string;
  node_id: string | null;
  node_name: string | null;
  provider_instance_id: string | null;
  desired_capacity_before: number | null;
  desired_capacity_target: number | null;
  activity_id: string | null;
  attempt_count: number;
  last_error: string | null;
  reason: string;
  requested_by: string;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
};

export type ComputePool = {
  id: string;
  name: string;
  pipeline_family: string;
  status: string;
  max_lease_batch: number;
  lease_seconds: number;
};

export type AuditEvent = {
  id: string;
  actor_subject: string;
  actor_role: string;
  action: string;
  object_type: string;
  object_id: string;
  reason: string;
  before_state: unknown | null;
  after_state: unknown | null;
  request_id: string | null;
  created_at: string;
};

export type StorageBackend = {
  id: string;
  version: number;
  name: string;
  provider: string;
  status: string;
  endpoint: string;
  public_download_endpoint: string;
  region: string | null;
  container: string;
  base_prefix: string;
  upload_prefix: string;
  output_prefix: string;
  date_partition_pattern: string;
  partition_timezone: string;
  result_url_ttl_seconds: number;
  credentials_configured: boolean;
  provider_config: Record<string, unknown>;
  created_at: string;
  is_default: boolean;
  default_version: number | null;
};

export type StorageGcSettings = {
  enabled: boolean;
  interval_seconds: number;
  next_run_at: string;
  last_started_at: string | null;
  last_finished_at: string | null;
  version: number;
  updated_at: string;
};

export type StorageGcRun = {
  id: string;
  trigger_type: "SCHEDULED" | "MANUAL";
  status: "PENDING" | "RUNNING" | "SUCCEEDED" | "PARTIAL_FAILED" | "FAILED";
  claimed_records: number;
  deleted_objects: number;
  external_objects: number;
  failed_objects: number;
  deleted_bytes: number;
  requested_by: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
};

export type StorageGcItem = {
  id: string;
  run_id: string;
  owner_kind: "RESULT" | "ATTEMPT";
  owner_id: string;
  backend_id: string;
  backend_version: number;
  provider: string;
  object_key: string;
  size_bytes: number | null;
  status: "RUNNING" | "DELETED" | "EXTERNAL_CLEANUP_REQUIRED" | "FAILED";
  error_message: string | null;
  started_at: string;
  finished_at: string | null;
};

export type TranscodeSettings = {
  version: number;
  mp3_bitrate_kbps: number;
  default_node_type: string;
  dsp_config: {
    ns_level: number;
    target_lufs: number;
    max_gain_db: number;
    limiter_threshold: number;
  };
  updated_at: string;
};

export type NodeType = {
  id: string;
  display_name: string;
  description: string;
  status: "ACTIVE" | "DISABLED";
  ready_nodes: number;
  free_slots: number;
};

export type ApiKeyMetadata = {
  key_id: string;
  key_prefix: string;
  scopes: string[];
  status: string;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
};

export type Tenant = {
  tenant_id: string;
  name: string;
  status: string;
  storage_version: number | null;
  upload_backend_id: string | null;
  upload_backend_version: number | null;
  output_backend_id: string | null;
  output_backend_version: number | null;
};
